import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Film,
  Gamepad2,
  ChefHat,
  Music,
  Play,
  Pause,
  X,
  Heart,
  MessageCircle,
  Sparkles,
  Users,
  Timer,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useVirtualDate, RoomType, GameType } from "@/hooks/useVirtualDate";
import { VirtualDateGame } from "./VirtualDateGame";

interface VirtualDateRoomProps {
  roomId: string;
  onClose: () => void;
}

const ROOM_TYPES: Record<RoomType, { icon: React.ReactNode; label: string; color: string }> = {
  movie: { icon: <Film className="w-5 h-5" />, label: "Watch Together", color: "from-purple-500 to-pink-500" },
  game: { icon: <Gamepad2 className="w-5 h-5" />, label: "Play Games", color: "from-green-500 to-emerald-500" },
  cooking: { icon: <ChefHat className="w-5 h-5" />, label: "Cook Together", color: "from-orange-500 to-red-500" },
  music: { icon: <Music className="w-5 h-5" />, label: "Listen Together", color: "from-blue-500 to-cyan-500" },
};

const GAMES: { type: GameType; label: string; description: string; icon: string }[] = [
  { type: "wouldyourather", label: "Would You Rather", description: "Choose between two options", icon: "🤔" },
  { type: "trivia", label: "Trivia Challenge", description: "Test your knowledge together", icon: "🧠" },
  { type: "truth_or_dare", label: "Truth or Dare", description: "Get to know each other", icon: "🎭" },
  { type: "twenty_questions", label: "20 Questions", description: "Guess what they're thinking", icon: "❓" },
  { type: "drawing", label: "Draw & Guess", description: "Show off your art skills", icon: "🎨" },
];

export const VirtualDateRoom = ({ roomId, onClose }: VirtualDateRoomProps) => {
  const {
    room,
    partner,
    currentActivity,
    loading,
    startRoom,
    endRoom,
    startActivity,
    submitResponse,
    completeActivity,
    isHost,
  } = useVirtualDate(roomId);

  const [selectedGame, setSelectedGame] = useState<GameType | null>(null);
  const [showGamePicker, setShowGamePicker] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Timer for active dates
  useEffect(() => {
    if (room?.status !== "active" || !room.started_at) return;

    const interval = setInterval(() => {
      const start = new Date(room.started_at!).getTime();
      const now = Date.now();
      setElapsedTime(Math.floor((now - start) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [room?.status, room?.started_at]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-xl flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Heart className="w-12 h-12 text-primary animate-bounce" />
          <p className="text-muted-foreground">Setting up your date...</p>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-xl flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Room not found</p>
          <Button onClick={onClose}>Go Back</Button>
        </div>
      </div>
    );
  }

  const roomConfig = ROOM_TYPES[room.room_type as RoomType] || ROOM_TYPES.game;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background overflow-hidden"
    >
      {/* Background gradient */}
      <div className={cn("absolute inset-0 bg-gradient-to-br opacity-20", roomConfig.color)} />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-4 py-3 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-xl bg-gradient-to-br", roomConfig.color)}>
            {roomConfig.icon}
          </div>
          <div>
            <h1 className="font-semibold text-foreground">{roomConfig.label}</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Users className="w-3 h-3" />
              <span>with {partner?.name || "Partner"}</span>
              {room.status === "active" && (
                <>
                  <span>•</span>
                  <Timer className="w-3 h-3" />
                  <span>{formatTime(elapsedTime)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {room.status === "active" && (
            <Button variant="destructive" size="sm" onClick={endRoom}>
              End Date
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 overflow-y-auto p-4 pb-24">
        {/* Waiting state */}
        {room.status === "waiting" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center min-h-[60vh] text-center"
          >
            <div className="relative mb-8">
              <Avatar className="w-24 h-24 border-4 border-primary/30">
                <AvatarImage src={partner?.photo_url || undefined} />
                <AvatarFallback className="text-2xl">{partner?.name?.[0]}</AvatarFallback>
              </Avatar>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute -bottom-2 -right-2 p-2 rounded-full bg-primary text-primary-foreground"
              >
                <Heart className="w-5 h-5" />
              </motion.div>
            </div>

            <h2 className="text-2xl font-bold text-foreground mb-2">
              Ready for your date with {partner?.name}?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-sm">
              {isHost
                ? "Start the date when you're both ready to have fun together!"
                : `Waiting for ${partner?.name} to start the date...`}
            </p>

            {isHost && (
              <Button size="lg" className="gap-2" onClick={startRoom}>
                <Play className="w-5 h-5" />
                Start Date
              </Button>
            )}
          </motion.div>
        )}

        {/* Active date */}
        {room.status === "active" && (
          <div className="space-y-6">
            {/* Current activity or game picker */}
            {currentActivity && !currentActivity.is_complete ? (
              <VirtualDateGame
                activity={currentActivity}
                partner={partner}
                isHost={isHost}
                onSubmitResponse={(response) => submitResponse(currentActivity.id, response)}
                onComplete={() => completeActivity(currentActivity.id)}
              />
            ) : showGamePicker ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-4"
              >
                <div className="text-center mb-6">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <h3 className="text-xl font-semibold">Pick a game to play</h3>
                  <p className="text-sm text-muted-foreground">Choose something fun to do together</p>
                </div>

                <div className="grid gap-3">
                  {GAMES.map((game) => (
                    <motion.button
                      key={game.type}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setSelectedGame(game.type);
                        startActivity(game.type);
                        setShowGamePicker(false);
                      }}
                      className="flex items-center gap-4 p-4 rounded-2xl bg-card hover:bg-accent transition-colors text-left border border-border/50"
                    >
                      <span className="text-3xl">{game.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">{game.label}</h4>
                        <p className="text-sm text-muted-foreground">{game.description}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-muted-foreground" />
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center min-h-[40vh] text-center"
              >
                <Gamepad2 className="w-16 h-16 text-primary/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">What would you like to do?</h3>
                <p className="text-muted-foreground mb-6">Pick an activity to enjoy together</p>
                <Button onClick={() => setShowGamePicker(true)} size="lg" className="gap-2">
                  <Sparkles className="w-5 h-5" />
                  Choose Activity
                </Button>
              </motion.div>
            )}
          </div>
        )}

        {/* Ended state */}
        {room.status === "ended" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center min-h-[60vh] text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="mb-6"
            >
              <Heart className="w-20 h-20 text-pink-500" fill="currentColor" />
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Great date! 💕</h2>
            <p className="text-muted-foreground mb-8 max-w-sm">
              Hope you had fun with {partner?.name}! Keep the conversation going in chat.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button className="gap-2">
                <MessageCircle className="w-4 h-4" />
                Message {partner?.name}
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </motion.div>
  );
};
